import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AdminAccess.css';

const TABS = [
  { value: 'pending',   labelKey: 'admin.accessTabPending' },
  { value: 'active',    labelKey: 'admin.accessTabActive' },
  { value: 'suspended', labelKey: 'admin.accessTabSuspended' },
  { value: 'banned',    labelKey: 'admin.accessTabBanned' },
];

/**
 * Admin: hantera Player.access_status — Sprint 3 av Fluxer-migrationen.
 *
 * Tabbat gränssnitt för pending/active/suspended/banned. Per spelare:
 * Steam/Discord-info, audit-fält (vem ändrade när, varför) och knappar
 * för grant/suspend/ban beroende på nuvarande status.
 *
 * Behörighet: 'access.grant'-permission via WebRole eller guild-owner.
 */
export default function AdminAccess({ perms, hideTitle = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('pending');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: tab });
      if (search.trim()) params.set('q', search.trim());
      const r = await fetch(`${API_URL}/api/admin/access?${params}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setPlayers(data.players || []);
      } else {
        setError(t('admin.loadPlayersFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setLoading(false);
    }
  }, [tab, search, getAuthHeaders, t]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const performAction = async (player, action, note) => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch(
        `${API_URL}/api/admin/access/${player.id}/${action}`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note || '' }),
        },
      );
      if (r.ok) {
        setConfirmModal(null);
        fetchPlayers();
      } else {
        const data = await r.json();
        setError(data.error || t('admin.actionFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setBusy(false);
    }
  };

  const renderActions = (player) => {
    const actions = [];
    if (player.access_status === 'pending') {
      actions.push({ key: 'grant', label: t('admin.actionGrant'), kind: 'primary' });
      actions.push({ key: 'ban', label: t('admin.actionBan'), kind: 'danger' });
    } else if (player.access_status === 'active') {
      actions.push({ key: 'suspend', label: t('admin.actionSuspend'), kind: 'warning' });
      actions.push({ key: 'ban', label: t('admin.actionBan'), kind: 'danger' });
    } else if (player.access_status === 'suspended') {
      actions.push({ key: 'grant', label: t('admin.actionReactivate'), kind: 'primary' });
      actions.push({ key: 'ban', label: t('admin.actionBan'), kind: 'danger' });
    } else if (player.access_status === 'banned') {
      // Banned players cannot be re-granted via this UI per backend rule.
      actions.push({ key: 'grant', label: t('admin.actionReactivateBlocked'), kind: 'disabled' });
    }
    return actions.map((a) => (
      <button
        key={a.key}
        className={`admin-btn admin-btn-${a.kind}`}
        onClick={() => setConfirmModal({ player, action: a.key, label: a.label })}
        disabled={a.kind === 'disabled'}
        type="button"
      >
        {a.label}
      </button>
    ));
  };

  return (
    <div className="admin-access">
      {!hideTitle && <h1 className="admin-section-title">{t('admin.accessTitle')}</h1>}
      <p className="admin-section-help">
        {t('admin.accessHelp')}
      </p>

      {error && <div className="admin-error">{error}</div>}

      <div className="access-tabs">
        {TABS.map((tb) => (
          <button
            key={tb.value}
            className={`access-tab${tab === tb.value ? ' access-tab-active' : ''}`}
            onClick={() => setTab(tb.value)}
            type="button"
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      <div className="admin-card">
        <input
          className="admin-input"
          type="text"
          placeholder={t('admin.accessSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : players.length === 0 ? (
        <div className="admin-info">{t('admin.noPlayers')}</div>
      ) : (
        <div className="access-list">
          {players.map((p) => (
            <div key={p.id} className="access-card">
              <div className="access-card-info">
                {p.steam_avatar && (
                  <img src={p.steam_avatar} alt="" className="access-avatar" />
                )}
                <div className="access-card-text">
                  <div className="access-card-name">{p.display_name}</div>
                  <div className="access-card-ids">
                    {p.steam_id && <span>Steam: {p.steam_id}</span>}
                    {p.discord_username && <span>Discord: {p.discord_username}</span>}
                  </div>
                  {p.access_note && (
                    <div className="access-card-note">{p.access_note}</div>
                  )}
                  {p.access_granted_at && (
                    <div className="access-card-meta">
                      {t('admin.lastChanged')}: {new Date(p.access_granted_at).toLocaleString('sv-SE')}
                    </div>
                  )}
                </div>
              </div>
              <div className="access-card-actions">{renderActions(p)}</div>
            </div>
          ))}
        </div>
      )}

      {confirmModal && (
        <ConfirmActionModal
          modal={confirmModal}
          busy={busy}
          onCancel={() => setConfirmModal(null)}
          onConfirm={(note) =>
            performAction(confirmModal.player, confirmModal.action, note)
          }
        />
      )}
    </div>
  );
}

/**
 * Bekräftelsemodal med valfri anteckning. Anteckning sparas i
 * Player.access_note och visas i framtida vyer som audit-spår.
 */
function ConfirmActionModal({ modal, busy, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  return (
    <div className="access-modal-backdrop" onClick={onCancel}>
      <div className="access-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="access-modal-title">
          {modal.label} — {modal.player.display_name}?
        </h3>
        <p className="access-modal-text">
          {modal.action === 'grant' && t('admin.confirmGrantText')}
          {modal.action === 'suspend' && t('admin.confirmSuspendText')}
          {modal.action === 'ban' && t('admin.confirmBanText')}
        </p>
        <textarea
          className="access-modal-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            modal.action === 'grant'
              ? t('admin.notePlaceholderGrant')
              : t('admin.notePlaceholderReason')
          }
          rows={3}
          maxLength={500}
        />
        <div className="access-modal-actions">
          <button
            className="admin-btn admin-btn-secondary"
            onClick={onCancel}
            disabled={busy}
            type="button"
          >
            {t('matchDetail.cancel')}
          </button>
          <button
            className={`admin-btn admin-btn-${modal.action === 'ban' ? 'danger' : 'primary'}`}
            onClick={() => onConfirm(note)}
            disabled={busy}
            type="button"
          >
            {busy ? t('admin.processing') : modal.label}
          </button>
        </div>
      </div>
    </div>
  );
}
